// apps/web/src/features/crm/leads/card/ArtifactsSystemSection.tsx
//
// "In system" subsection of the "Artifacts" card (#2316, D-1/D-2; #2318
// D-3). Renders the Lead's linked system entities — China/Russia requests,
// Deals, and CP (commercial proposals) — from `useLeadArtifacts` (#2314) as
// `FileRow`s, plus the call-center intake history (full detail, from
// `useLeadIntakeHistory`) as expandable rows below them. `entities` arrives
// RAW (unfiltered) from the caller; this component owns the "only render
// kinds this issue knows how to render" allowlist, so the filter sits right
// next to the render it protects.
//
// ROLLING-COMPAT (#2314 WHY, D-4): `kind` is an open string — a future slice
// may emit a kind this build has never heard of. Any kind outside the
// known-renderable set is silently dropped here — never a crash, never an
// error row. The caller (`ArtifactsBlock`) folds a `useLeadArtifacts`
// load/error into an empty `entities` array, so the rollback-degrade (D-4)
// needs no special case here either — an empty list is just an empty list.
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { LeadArtifactEntity, LeadIntakeSubmissionHistoryItem } from '@magimp/shared';
import { ErrorState, FileRow, LoadingState, type IconName } from '../../../../ui/index.js';
import { formatDateTime } from '../../../../i18n/index.js';
import { errorMessage } from '../../../../api/errors.js';
import { IntakeArtifactRow } from './IntakeArtifactRow.js';

export const RENDERABLE_ARTIFACT_KINDS = ['request', 'russia-request', 'deal', 'proposal'] as const;
export type RenderableArtifactKind = (typeof RENDERABLE_ARTIFACT_KINDS)[number];

export function isRenderableArtifactKind(kind: string): kind is RenderableArtifactKind {
  return (RENDERABLE_ARTIFACT_KINDS as readonly string[]).includes(kind);
}

const KIND_ICON: Record<RenderableArtifactKind, IconName> = {
  request: 'ship',
  'russia-request': 'truck',
  deal: 'handshake',
  proposal: 'file-text',
};

export interface ArtifactsSystemSectionProps {
  /** Raw, unfiltered `useLeadArtifacts` entities — may include `intake`, `proposal`, or an unknown future kind. */
  entities: readonly LeadArtifactEntity[];
  intakeItems: readonly LeadIntakeSubmissionHistoryItem[];
  intakePending: boolean;
  intakeError: unknown;
  onRetryIntake: () => void;
}

export function ArtifactsSystemSection({
  entities,
  intakeItems,
  intakePending,
  intakeError,
  onRetryIntake,
}: ArtifactsSystemSectionProps) {
  const { t } = useTranslation(['leads', 'common']);
  const navigate = useNavigate();
  const known = entities.filter((e): e is LeadArtifactEntity & { kind: RenderableArtifactKind } =>
    isRenderableArtifactKind(e.kind),
  );

  const hasContent = known.length > 0 || intakePending || !!intakeError || intakeItems.length > 0;
  if (!hasContent) return null;

  return (
    <div
      data-testid="lead-artifacts-system"
      style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <div
        style={{
          fontSize: 10,
          color: 'var(--fg-4)',
          textTransform: 'uppercase',
          letterSpacing: '.08em',
          marginBottom: 2,
        }}
      >
        {t('leads:card.artifacts.system')}
      </div>
      {known.map((e) => (
        <FileRow
          key={e.id}
          icon={KIND_ICON[e.kind]}
          label={
            e.code
              ? `${t(`leads:card.artifacts.kind.${e.kind}`)} · ${e.code}`
              : t(`leads:card.artifacts.kind.${e.kind}`)
          }
          note={formatDateTime(e.date)}
          // D-3 (#2318): only the CP row navigates (to its own workspace
          // drawer); request/russia-request/deal navigation is out of scope.
          // `exactOptionalPropertyTypes` forbids `onClick={undefined}` (the
          // prop must be OMITTED for a non-navigating row, not set to
          // undefined), hence the conditional spread.
          {...(e.kind === 'proposal' ? { onClick: () => navigate(`/kp/${e.id}`) } : {})}
        />
      ))}
      {intakePending && <LoadingState />}
      {!intakePending && !!intakeError && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(intakeError)}
          onRetry={onRetryIntake}
        />
      )}
      {!intakePending &&
        !intakeError &&
        intakeItems.map((item) => <IntakeArtifactRow key={item.id} item={item} />)}
    </div>
  );
}
